// The desk operator (DESK-OPERATOR-AND-MAIL-BRIEF §2): who is at the keyboard of
// a shared desk login. A platform concern by the same test as capabilities:
// every context's audited writes need it, and none may answer it differently.
// Pure — the port, the session holder and the label; the adapters are at
// `@lemontaigu/platform/adapters/client` (the desk) and `/adapters/edge` (the
// identify Function).

/** The header the desk sends on every database request; the database reads it
 *  in `desk_operator_resolve()`. */
export const DESK_OPERATOR_HEADER = 'X-Desk-Operator';

/** A person on the desk list (`desk_operator_list`). */
export interface DeskOperatorEntry {
  id: string;
  firstName: string;
  lastName: string;
  nickname: string | null;
  /** No PIN yet: the operator sets it (Équipe › Pointages, Hors Staff) before they can identify. */
  hasPin: boolean;
  /** Locked after 5 wrong PINs (D8), until then; null when not locked. */
  lockedUntil: string | null;
}

/** Who the current request resolves to (`desk_operator_current`). */
export interface DeskOperatorCurrent {
  employeeId: string;
  firstName: string;
  lastName: string;
  nickname: string | null;
  /** Through a desk session (« Changer » ends it), or the login's own row. */
  viaSession: boolean;
}

export type DeskIdentifyRefusal = 'notListed' | 'locked' | 'noPin' | 'badPin' | 'refused' | 'unavailable';

export type DeskIdentifyResult =
  | { ok: true; sessionId: string }
  | { ok: false; reason: DeskIdentifyRefusal; lockedUntil?: string };

/** The desk's port: the list, who is identified, identify, « Changer ». */
export interface DeskOperatorStore {
  list(): Promise<readonly DeskOperatorEntry[]>;
  current(): Promise<DeskOperatorCurrent | null>;
  identify(employeeId: string, pin: string): Promise<DeskIdentifyResult>;
  end(): Promise<void>;
  /**
   * The database refused a write for want of a person (SQLSTATE `DK001`, O3):
   * `handler` opens « Qui êtes-vous ? » and resolves true once someone has
   * identified, and the refused request is sent again. Returns the unregister.
   */
  onIdentifyRequired(handler: () => Promise<boolean>): () => void;
}

/** The SQLSTATE `desk_operator_stamp()` raises when an audited write has no person. */
export const DESK_OPERATOR_REQUIRED_CODE = 'DK001';

/** What a refusal says to the person at the desk (French, staff-facing). */
export const DESK_IDENTIFY_REFUSAL_FR: Record<DeskIdentifyRefusal, string> = {
  notListed: 'Cette personne n’est pas sur la liste de la réception.',
  locked: 'Trop d’essais : ce nom est bloqué quelques minutes.',
  noPin: 'Pas encore de code : à demander à la direction (Équipe › Pointages).',
  badPin: 'Code incorrect.',
  refused: 'Identification refusée pour ce compte.',
  unavailable: 'Identification indisponible pour le moment.',
};

/** « Marie D. » — the nickname when there is one, else the first name, and the
 *  last name's initial. */
export function deskOperatorLabel(p: { firstName: string; lastName: string; nickname: string | null }): string {
  const first = (p.nickname ?? '').trim() || p.firstName.trim();
  const initial = p.lastName.trim().charAt(0).toUpperCase();
  return initial ? `${first} ${initial}.` : first;
}

/** Where a tab keeps its session id. `sessionStorage` in the browser; anything
 *  with the same three methods in a test. Every access may throw (a private
 *  window, blocked storage): the holder then keeps the id in memory only. */
export interface SessionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface DeskOperatorSessionHolder {
  get(): string | null;
  set(sessionId: string | null): void;
  subscribe(listener: (sessionId: string | null) => void): () => void;
  /** Who answers a DK001 refusal (the mounted « Qui êtes-vous ? »); null when none is mounted. */
  requiredHandler(): (() => Promise<boolean>) | null;
  setRequiredHandler(handler: (() => Promise<boolean>) | null): void;
}

const STORAGE_KEY = 'lemontaigu.deskOperatorSession';

/** One holder per storage: bos.* mounts the PMS and reception tabs, each with
 *  its own client and holder built on the same `sessionStorage`. Two holders
 *  were two people — « Qui êtes-vous ? » set bos's, and the PMS writes went out
 *  without the header and without anyone to ask (DK001 on « Enregistrer »,
 *  2026-10-27). The tab has one desk session, so its holders are one. */
const holders = new WeakMap<SessionStorageLike, DeskOperatorSessionHolder>();

/** The tab's desk session id: read by the fetch wrapper on every request, set
 *  by « Qui êtes-vous ? », cleared by « Changer ». Per tab, so two people on
 *  two phones under the same login stay two people. */
export function createDeskOperatorSessionHolder(storage: SessionStorageLike | null = null): DeskOperatorSessionHolder {
  if (storage === null) return newHolder(null);
  const shared = holders.get(storage);
  if (shared) return shared;
  const holder = newHolder(storage);
  holders.set(storage, holder);
  return holder;
}

function newHolder(storage: SessionStorageLike | null): DeskOperatorSessionHolder {
  let current: string | null = null;
  try {
    current = storage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    current = null;
  }
  const listeners = new Set<(sessionId: string | null) => void>();
  let required: (() => Promise<boolean>) | null = null;
  return {
    requiredHandler: () => required,
    setRequiredHandler(handler) {
      required = handler;
    },
    get: () => current,
    set(sessionId) {
      current = sessionId;
      try {
        if (sessionId === null) storage?.removeItem(STORAGE_KEY);
        else storage?.setItem(STORAGE_KEY, sessionId);
      } catch {
        // Memory only: the identification lasts the page.
      }
      for (const l of listeners) l(sessionId);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
